
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
    AUTH_SESSION_CLEARED_EVENT,
    clearStoredSession,
    getIdleTimeoutMs,
    LAST_ACTIVITY_STORAGE_KEY,
    readPharmacyIdFromSession,
    readSelectedPharmacy,
    readStoredAuth,
    writeSelectedPharmacy,
    writeStoredAuth,
    toSessionProfile,
} from "./authStorage";
import { logoutSession } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(readStoredAuth);
    const [selectedPharmacy, setSelectedPharmacyState] = useState(readSelectedPharmacy);

    // sessionData : réponse de /api/auth/login. Le JWT est dans le cookie HttpOnly, jamais ici (SEC-008).
    const login = useCallback((sessionData) => {
        const auth = toSessionProfile(sessionData);
        writeStoredAuth(auth);
        setUser(auth);
        // Un changement d'utilisateur purge la pharmacie sélectionnée du précédent.
        setSelectedPharmacyState(readSelectedPharmacy());
    }, []);

    const logout = useCallback((expired = false) => {
        clearStoredSession({ expired, notify: false });
        // Le cookie HttpOnly ne peut être effacé que par le serveur.
        Promise.resolve(logoutSession()).catch(() => {});
        setUser(null);
        setSelectedPharmacyState(null);
    }, []);

    useEffect(() => {
        const syncClearedSession = () => {
            setUser(null);
            setSelectedPharmacyState(null);
        };
        window.addEventListener(AUTH_SESSION_CLEARED_EVENT, syncClearedSession);
        return () => window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, syncClearedSession);
    }, []);

    useEffect(() => {
        if (!user) return;

        const expireIfIdle = () => {
            const lastActivity = Number.parseInt(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || "0", 10);
            if (!Number.isFinite(lastActivity) || lastActivity <= 0) {
                localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString());
                return false;
            }
            const idleTimeoutMs = getIdleTimeoutMs(user.role);
            if (Date.now() - lastActivity > idleTimeoutMs) {
                logout(true);
                return true;
            }
            return false;
        };
        const updateActivity = () => {
            if (!expireIfIdle()) {
                localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString());
            }
        };
        const updateVisibleActivity = () => {
            if (document.visibilityState !== "hidden") updateActivity();
        };
        updateActivity();

        const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
        events.forEach(e => document.addEventListener(e, updateActivity, { passive: true }));
        window.addEventListener("focus", updateActivity);
        window.addEventListener("pageshow", updateActivity);
        document.addEventListener("visibilitychange", updateVisibleActivity);

        const interval = setInterval(() => {
            expireIfIdle();
        }, 60_000);

        return () => {
            events.forEach(e => document.removeEventListener(e, updateActivity));
            window.removeEventListener("focus", updateActivity);
            window.removeEventListener("pageshow", updateActivity);
            document.removeEventListener("visibilitychange", updateVisibleActivity);
            clearInterval(interval);
        };
    }, [user, logout]);

    const selectPharmacy = useCallback((pharmacy) => {
        writeSelectedPharmacy(pharmacy);
        setSelectedPharmacyState(pharmacy ?? null);
    }, []);

    // pharmacyId effectif : selectedPharmacy pour le super-admin, sinon celui du profil de session
    const pharmacyId = selectedPharmacy?.id ?? readPharmacyIdFromSession();

    const contextValue = useMemo(
        () => ({ user, login, logout, selectedPharmacy, selectPharmacy, pharmacyId }),
        [user, login, logout, selectedPharmacy, selectPharmacy, pharmacyId]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
